import { useState } from 'react';
import type { KnowledgeQueryResult } from '@shared/knowledge';

interface SourceCardProps {
  chunk: KnowledgeQueryResult;
  index: number;
}

const CATEGORY_STYLES: Record<string, string> = {
  process: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  decision: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  stakeholder: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800',
  tacit: 'bg-rose-100 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-800',
  context: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
};

export function SourceCard({ chunk, index }: SourceCardProps) {
  const [expanded, setExpanded] = useState(false);

  const similarityPercent = Math.round(chunk.similarityScore * 100);

  return (
    <div className="rounded-xl border border-border bg-muted/40 transition-colors hover:bg-muted/60">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-start gap-3 px-4 py-3 text-left"
        aria-expanded={expanded}
        aria-label={`Source ${index + 1}: ${chunk.content.slice(0, 60)}`}
      >
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-kaiva-teal text-[10px] font-bold text-white">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground line-clamp-2">
            {chunk.content}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            {chunk.categories.map((category) => (
              <span
                key={category}
                className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                  CATEGORY_STYLES[category] ?? 'bg-gray-100 text-gray-600 border-gray-200'
                }`}
              >
                {category}
              </span>
            ))}
            <span className="text-[10px] text-muted-foreground">
              {similarityPercent}% match
            </span>
          </div>
        </div>
        <svg
          className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
            expanded ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {expanded && (
        <div className="border-t border-border px-4 py-3 space-y-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Source Quote
            </p>
            <blockquote className="mt-1 border-l-2 border-kaiva-coral/40 pl-3 text-sm italic text-muted-foreground">
              &ldquo;{chunk.sourceQuote}&rdquo;
            </blockquote>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
            <span>
              <span className="font-semibold">Interviewee:</span>{' '}
              {chunk.sessionMetadata.intervieweeName}
            </span>
            <span>
              <span className="font-semibold">Structure:</span>{' '}
              {chunk.sessionMetadata.structureName}
            </span>
            {chunk.sessionMetadata.completedAt && (
              <span>
                <span className="font-semibold">Date:</span>{' '}
                {new Date(chunk.sessionMetadata.completedAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
